import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Download, Plus, Droplets, X } from 'lucide-react';
import { useWatermark, type WatermarkConfig } from '@/hooks/useWatermark';
import { BeforeAfterComparison } from './BeforeAfterComparison';
import type { UploadedImage } from '@/pages/ImageToolsHub';

interface WatermarkToolProps {
  images: UploadedImage[];
}

export const WatermarkTool: React.FC<WatermarkToolProps> = ({ images }) => {
  const [watermarks, setWatermarks] = useState<WatermarkConfig[]>([{
    type: 'text',
    text: 'Watermark',
    position: 'bottom-right',
    opacity: 0.5,
    scale: 1,
    rotation: 0,
    fontSize: 48,
    fontColor: '#FFFFFF',
    fontFamily: 'Arial',
    tile: false,
  }]);
  
  const { isProcessing, progress, watermarkedImages, processBatch, clearResults } = useWatermark();
  
  const selectedImages = images.filter(img => img.selected);

  const handleProcess = async () => {
    if (selectedImages.length === 0 || watermarks.length === 0) return;
    
    const files = selectedImages.map(img => img.file);
    await processBatch(files, watermarks);
  };

  const handleDownload = (url: string, name: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `watermarked-${name}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadAll = () => {
    watermarkedImages.forEach((img, index) => {
      setTimeout(() => {
        handleDownload(img.watermarked, `image-${index + 1}.png`);
      }, index * 100);
    });
  };

  const addWatermark = () => {
    setWatermarks([...watermarks, {
      type: 'text',
      text: 'Watermark',
      position: 'center',
      opacity: 0.5,
      scale: 1,
      rotation: 0,
      fontSize: 48,
      fontColor: '#FFFFFF',
      fontFamily: 'Arial',
      tile: false,
    }]);
  };

  const removeWatermark = (index: number) => {
    setWatermarks(watermarks.filter((_, i) => i !== index));
  };

  const updateWatermark = (index: number, updates: Partial<WatermarkConfig>) => {
    setWatermarks(watermarks.map((wm, i) => i === index ? { ...wm, ...updates } : wm));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Watermark Settings</CardTitle>
              <CardDescription>
                Add text or image watermarks to your images
              </CardDescription>
            </div>
            <Button onClick={addWatermark} variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Watermark
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {watermarks.map((watermark, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Watermark {index + 1}</CardTitle>
                  {watermarks.length > 1 && (
                    <Button
                      onClick={() => removeWatermark(index)}
                      variant="ghost"
                      size="sm"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <Tabs
                  value={watermark.type}
                  onValueChange={(value) => updateWatermark(index, { type: value as 'text' | 'image' })}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="text">Text</TabsTrigger>
                    <TabsTrigger value="image">Image</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="text" className="space-y-4">
                    <div className="space-y-2">
                      <Label>Text</Label>
                      <Input
                        value={watermark.text || ''}
                        onChange={(e) => updateWatermark(index, { text: e.target.value })}
                        placeholder="Enter watermark text"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Font Size: {watermark.fontSize}px</Label>
                        <Slider
                          min={12}
                          max={200}
                          value={[watermark.fontSize || 48]}
                          onValueChange={(value) => updateWatermark(index, { fontSize: value[0] })}
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Color</Label>
                        <Input
                          type="color"
                          value={watermark.fontColor || '#FFFFFF'}
                          onChange={(e) => updateWatermark(index, { fontColor: e.target.value })}
                        />
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="image" className="space-y-4">
                    <div className="space-y-2">
                      <Label>Watermark Image</Label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => updateWatermark(index, { imageFile: e.target.files?.[0] })}
                      />
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="space-y-2">
                  <Label>Position</Label>
                  <Select
                    value={watermark.position}
                    onValueChange={(value) => updateWatermark(index, { position: value as any })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="top-left">Top Left</SelectItem>
                      <SelectItem value="top-right">Top Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Opacity: {Math.round(watermark.opacity * 100)}%</Label>
                    <Slider
                      min={0}
                      max={1}
                      step={0.01}
                      value={[watermark.opacity]}
                      onValueChange={(value) => updateWatermark(index, { opacity: value[0] })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Scale: {watermark.scale.toFixed(1)}x</Label>
                    <Slider
                      min={0.1}
                      max={3}
                      step={0.1}
                      value={[watermark.scale]}
                      onValueChange={(value) => updateWatermark(index, { scale: value[0] })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Rotation: {watermark.rotation}°</Label>
                    <Slider
                      min={-180}
                      max={180}
                      value={[watermark.rotation]}
                      onValueChange={(value) => updateWatermark(index, { rotation: value[0] })}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor={`tile-${index}`}>Tile Pattern</Label>
                  <Switch
                    id={`tile-${index}`}
                    checked={watermark.tile || false}
                    onCheckedChange={(checked) => updateWatermark(index, { tile: checked })}
                  />
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="flex gap-2">
            <Button
              onClick={handleProcess}
              disabled={selectedImages.length === 0 || watermarks.length === 0 || isProcessing}
              className="flex-1"
            >
              <Droplets className="w-4 h-4 mr-2" />
              {isProcessing ? 'Processing...' : `Apply Watermark (${selectedImages.length})`}
            </Button>
            
            {watermarkedImages.length > 0 && (
              <Button onClick={clearResults} variant="outline">
                Clear Results
              </Button>
            )}
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                {Math.round(progress)}% complete
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {watermarkedImages.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Results ({watermarkedImages.length})</CardTitle>
              <Button onClick={handleDownloadAll} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Download All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {watermarkedImages.map((img) => (
                <div key={img.id}>
                  <BeforeAfterComparison
                    beforeImage={img.original}
                    afterImage={img.watermarked}
                  />
                  <Button
                    onClick={() => handleDownload(img.watermarked, 'image.png')}
                    variant="outline"
                    size="sm"
                    className="mt-2"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
